import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { NotFound } from '@stratumn/atomic';

import { graphql } from '@apollo/client/react/hoc';
import * as Sentry from '@sentry/react';
import { fromLinkObject } from '@stratumn/js-chainscript';
import to from 'await-to-js';
import gql from 'graphql-tag';
import compose from 'lodash.flowright';
import Path from 'path-to-regexp';
import PropType from 'prop-types';
import qs from 'query-string';
import { flushSync } from 'react-dom';
import { withRouter } from 'react-router-dom';

import { logout } from '@/client/account';
import {
  clearBatchSelection,
  readBatchSelection
} from '@/components/batchAction/batchHandoff';
import {
  withLeavingAlertContext,
  withLeavingAlertProvider
} from '@/components/beforeLeavingAlert';
import { Navbar } from '@/components/layouts';
import SignLinkDialog from '@/components/signLinkDialog';
import { CustomToast, notify } from '@/components/toast';
import TraceIconSpinner from '@/components/ui/traceIconSpinner';
import { TOOLTIP_PORTAL, TRAY_PORTAL_RIGHT } from '@/constant/htmlIds';
import {
  ROUTE_INSPECT_TRACE,
  ROUTE_INSPECT_TRACE_LINK,
  ROUTE_LOGIN,
  ROUTE_WORKFLOW_DASHBOARD,
  ROUTE_WORKFLOW_OVERVIEW
} from '@/constant/routes';
import { useReAuth, withUser } from '@/contexts';
import { traceClient } from '@/gql';
import { isSiebelSessionExpired } from '@/gql/client/siebelSessionExpired';
import { useCreateBatchAction } from '@/hooks/useCreateBatchAction';
import { deepGet } from '@/utils';
import { useFunctionAsState } from '@/utils/hooks';
import { buildWorkflowContext } from '@/utils/workflowContext';
import { SpanType, withSpanAsync } from '../../tracing';
import AnswerGroupDialog from './answerGroupDialog';
import Batch from './batch';
import { DataTool } from './dataTool';
import Form from './form';
import fragments from './fragments';
import Signature from './signatureTool';

export const NewLink = memo(props => {
  const {
    match,
    history,
    location,
    actionQuery,
    createLinksMutation,
    setAskBeforeLeaving,
    setLeavingAlertModalConfig,
    user,
    answer = false,
    answerData = {},
    refetchTraceQuery = () => {}
  } = props;

  const { workflowByRowId: workflow = {}, loading } = actionQuery;

  const { rowId: workflowId, rawActionByKey, actionByKey } = workflow;
  const action = rawActionByKey ? { ...rawActionByKey, ...actionByKey } : null;

  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [answerGroupDialogOpen, setAnswerGroupDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [formData, setFormData] = useState(null);
  const [mutationProgress, setMutationProgress] = useState(false);
  const [cleanupFn, setCleanupFn] = useFunctionAsState(null);

  // Used to re-authenticate against Siebel in place when its token expires
  // during a submission, so the user does not lose their form progress.
  const { requestSiebelReAuth } = useReAuth();

  // Scalable Batch Actions (STMN-3359): server-side batch submission. Only used
  // when we arrived here with a batch selection (see `batchSelection` below).
  const createBatchAction = useCreateBatchAction();

  // --- Batch selection handoff.
  // The trace ids never travel in the query string: ~215 uuids already overflow
  // the 8 KB ingress URL limit. They arrive in the router state, with a
  // sessionStorage stash (keyed by `batchKey`) as the reload/new-tab fallback.
  // `traceCount` describes the selection, since only one trace is hydrated.
  const { batchKey, traceCount: traceCountParam } = useMemo(
    () => qs.parse(location.search),
    [location.search]
  );
  const batchTraceCount = traceCountParam ? Number(traceCountParam) : null;

  const batchSelection = useMemo(() => {
    if (location.state?.batch) return location.state.batch;
    if (batchKey) return readBatchSelection(batchKey);
    return null;
  }, [location.state, batchKey]);

  // A batchKey with no recoverable selection must NOT fall through to the
  // per-trace client-signing path: that would sign the whole batch in the
  // browser and submit it as one transaction.
  const batchSelectionLost = !!batchKey && !batchSelection;

  useEffect(() => {
    if (!batchSelectionLost) return;
    notify.error(
      <CustomToast
        description="Please select the traces again to run this action."
        title="Batch selection lost"
        type="error"
      />
    );
    setAskBeforeLeaving?.(false);
    history.replace(
      Path.compile(ROUTE_WORKFLOW_OVERVIEW)({ id: match.params.wfid })
    );
  }, [batchSelectionLost, history, match.params.wfid, setAskBeforeLeaving]);

  // Set document title and BeforeLeaving Alert content based on workflow data
  useEffect(() => {
    const {
      name: workflowName,
      rawActionByKey: { title: actionTitle } = {},
      traces: { nodes: traces = [] } = {}
    } = workflow;

    if (actionTitle) {
      const nbTraces = batchTraceCount ?? traces.length;
      let traceName = null;
      if (nbTraces > 0) {
        traceName = 'Batch update';
        if (nbTraces === 1 && traces[0]) {
          traceName = traces[0].name;
        }
      }
      document.title = `${actionTitle}${
        traceName ? ` - ${traceName}` : ''
      } - ${workflowName} - Trace`;

      setLeavingAlertModalConfig?.({
        title: (
          <p>
            Quit <span className="text-primary">{actionTitle}</span> action
          </p>
        ),
        content: (
          <div>
            <p>
              You&apos;re about to quit the{' '}
              <b className="font-bold text-primary">{actionTitle}</b> action
              without saving your changes.
            </p>
            <br className="select-none" />
            <p>All unsaved changes could be lost, are you sure?</p>
          </div>
        ),
        confirmBtnText: 'Quit action'
      });
    }
  }, [workflow, batchTraceCount]);

  const toggleSignDialog = useCallback(
    () => setSignDialogOpen(!signDialogOpen),
    [signDialogOpen, setSignDialogOpen]
  );

  const toggleAnswerGroupDialog = useCallback(
    () => setAnswerGroupDialogOpen(!answerGroupDialogOpen),
    [answerGroupDialogOpen, setAnswerGroupDialogOpen]
  );

  const goToHome = () => history.push(ROUTE_WORKFLOW_DASHBOARD);

  const goToWorkflowOverview = () => {
    history.push(
      Path.compile(ROUTE_WORKFLOW_OVERVIEW)({ id: match.params.wfid })
    );
  };

  const goToNewLink = async ({ links, firstlinkHash }) => {
    if (action.key === 'answerComment') {
      toggleSignDialog();
      // refetching trace query to allow display of the very first answer
      // saved to the trace.
      refetchTraceQuery();
      return;
    }
    if (links[0]?.link?.meta) {
      const { mapId } = links[0].link.meta;
      history.replace(
        Path.compile(ROUTE_INSPECT_TRACE_LINK)({
          id: mapId,
          linkid: firstlinkHash
        })
      );
      return;
    }
    // by default go back to workflow overview
    goToWorkflowOverview();
  };

  // build the workflow context passed to each interface
  const workflowContext = useMemo(() => {
    const { groupKey: authorGroupLabel } = qs.parse(location.search);
    return buildWorkflowContext(workflow, authorGroupLabel);
  }, [workflow, location]);

  // tracesList is a list of { traceId, formData }
  const sendLinksRequest = async (signingKey, tracesList) => {
    // 1 - init links for all selected traces
    // add the index as a 'cacheBuster' field to the variables so that Apollo
    // never sees the initAttestationLink queries as duplicates
    // even when all required variables are the same
    const links = await Promise.all(
      tracesList.map(async ({ traceId, formData: traceFormData }, idx) => {
        const {
          data: { initAttestationLink }
        } = await traceClient.query({
          query: INIT_ATTESTATION_QUERY,
          variables: {
            traceId,
            formData: traceFormData,
            actionKey: action.key,
            groupId: answer ? selectedGroup : workflowContext.author.rowId,
            cacheBuster: idx
          },
          fetchPolicy: 'no-cache'
        });
        const newLink = fromLinkObject(initAttestationLink.link);
        newLink.sign(signingKey, '[version,data,meta]');
        return { ...newLink, data: traceFormData };
      })
    );

    // 2 - send them in a batch
    const createLinksResponse = await createLinksMutation({
      variables: { input: links }
    });

    return {
      links,
      firstlinkHash: createLinksResponse?.data?.createLinks?.links[0]?.linkHash
    };
  };

  const createLinks = async (signingKey, formDataTmp = formData) => {
    await withSpanAsync('newLinks', SpanType.mutation, async () => {
      const { traceIds } = qs.parse(location.search);
      let tracesList;
      if (traceIds) {
        // if traceIds is not null this is an action to update existing traces
        // check whether this is an actual batch by checking the number of ','-separated trace ids
        tracesList = traceIds
          .split(',')
          .map(traceId => ({ traceId, formData: formDataTmp }));
      } else if (answerData?.traceId) {
        const dataWithLinkHash = {
          commentLinkHash: answerData.actionLinkHash,
          ...formDataTmp
        };
        tracesList = answerData.traceId
          .split(',')
          .map(traceId => ({ traceId, formData: dataWithLinkHash }));
      } else {
        // no traceIds provided so this is a trace creation
        // action being batchable is driven by its config
        // and if action is batched the formData is actually a list
        tracesList = action.canBatch
          ? formDataTmp.map(({ data }) => ({ formData: data }))
          : [{ formData: formDataTmp }]; // otherwise formData is the actual form data to submit
      }
      setMutationProgress(true);
      const toastId = notify.loading('Submitting action...');

      Sentry.setContext('Link info', {
        traceId: tracesList[0].traceId,
        workflowId,
        formData: JSON.stringify(tracesList[0].formData, null, 2),
        actionKey: action.key
      });

      Sentry.setTag('Workflow', workflow.name);
      Sentry.setTag('ActionKey', action.key);

      const [err, res] = await to(sendLinksRequest(signingKey, tracesList));

      // catch potential errors of the sendLinksRequest function
      if (err) {
        setMutationProgress(false);

        if (isSiebelSessionExpired(err?.graphQLErrors, err?.networkError)) {
          notify.dismiss(toastId);
          toggleSignDialog();
          const reAuthenticated = await requestSiebelReAuth();
          if (reAuthenticated) {
            await createLinks(signingKey, formDataTmp);
          }
          return;
        }

        const errorMessage = err?.networkError?.result?.errors?.[0]?.message;
        notify.error(
          <CustomToast
            description={errorMessage ?? err?.message ?? 'Unknown error'}
            title="Action submission failed"
            type="error"
          />,
          { id: toastId }
        );

        const isAuthError = err?.networkError?.statusCode === 401;
        if (isAuthError) {
          flushSync(() => {
            setAskBeforeLeaving?.(false);
          });
          setLeavingAlertModalConfig(false);

          // Logout and redirect to login page
          await logout();
          history.push(ROUTE_LOGIN);
        }
        toggleSignDialog();
        return;
      }

      notify.success('Action submitted', { id: toastId });

      // if a cleanup function has been set by the action interface provider
      // (eg data tool cleanu local storage if successful)
      // call it now
      if (cleanupFn) cleanupFn();

      // Since the new link was sent, we disable the "Before leaving alert" so it won't ask user confirmation (before changing the route)
      // Force state update with flushSync
      // https://react.dev/reference/react-dom/flushSync
      flushSync(() => {
        setAskBeforeLeaving?.(false);
      });
      goToNewLink(res);
      setMutationProgress(false);
    });
  };

  // --- Scalable Batch Actions (STMN-3359).
  // When we arrived here from the overview's batch tray, `batchSelection` (see
  // above) carries the selection (explicit ids or a filter). In that case a valid
  // form submit runs a server-side batch (the server signs each link via
  // impersonateEmail) instead of the per-trace client-signing flow. We submit
  // here — in place, mirroring `createLinks` — and only navigate to the overview
  // afterwards, passing the created `jobId` so the progress tray opens.
  const submitBatch = useCallback(
    async formDataTmp => {
      const { groupKey: groupLabel } = qs.parse(location.search);
      const batchAction = {
        workflowId: String(workflowId),
        actionKey: action.key,
        // Human action title (falls back to the key) for the progress toast;
        // carried in the handoff since batchJob(id) does not return the action.
        actionTitle: action.title ?? action.key,
        // Human workflow name, persisted on the job for the digest email.
        workflowName: workflow.name,
        groupLabel,
        formData: formDataTmp ?? {}
      };

      // Await the submission BEFORE releasing the leaving guard + navigating —
      // exactly like createLinks. The await moves the flushSync + history.push
      // into a microtask, outside React's synchronous submit dispatch, so
      // react-router's <Prompt> reliably unblocks first. Doing it synchronously
      // in the event handler leaves the Prompt blocking and pops the "Quit
      // action" modal instead of navigating.
      const jobId = await createBatchAction({
        action: batchAction,
        selection: batchSelection
      });

      // Clear the form backup + leaving guard now that the job is submitted.
      if (cleanupFn) cleanupFn();
      if (batchKey) clearBatchSelection(batchKey);
      flushSync(() => {
        setAskBeforeLeaving?.(false);
      });

      history.push(Path.compile(ROUTE_WORKFLOW_OVERVIEW)({ id: workflowId }), {
        batchJobId: jobId
      });
    },
    [
      location.search,
      workflowId,
      workflow.name,
      action,
      batchSelection,
      batchKey,
      createBatchAction,
      cleanupFn,
      setAskBeforeLeaving,
      history
    ]
  );

  const header = useMemo(() => {
    if (!(workflow && action)) return <Navbar />;

    let traceLink;
    const nbTraces = batchTraceCount ?? workflow.traces.nodes.length;
    const [hydratedTrace] = workflow.traces?.nodes ?? [];

    // A filter-mode batch hydrates no trace, even when it matches exactly one:
    // without a rowId `Path.compile` throws instead of rendering a breadcrumb.
    if (nbTraces === 1 && hydratedTrace?.rowId) {
      traceLink = {
        icon: 'Trace',
        label: hydratedTrace.name ?? 'Trace',
        path: Path.compile(ROUTE_INSPECT_TRACE)({ id: hydratedTrace.rowId })
      };
    } else if (nbTraces >= 1) {
      traceLink = {
        label: 'Batch update'
      };
    }

    const rawActionByKeyLabel = {
      icon: workflow.rawActionByKey?.icon ?? null,
      label: `${deepGet(workflow, 'rawActionByKey.title', null)} | ${deepGet(
        workflowContext,
        'author.name'
      )}`
    };

    const bottomLevelLinks = [
      {
        icon: 'TableColumns',
        label: workflow.name ?? 'Workflow',
        path: Path.compile(ROUTE_WORKFLOW_OVERVIEW)({
          id: workflow.rowId
        })
      },
      ...(traceLink ? [traceLink] : []),
      rawActionByKeyLabel
    ];

    const configNavbar = {
      loading,
      bottomLevel: {
        workflowPage: true,
        infoContext: { links: bottomLevelLinks }
      }
    };

    return <Navbar config={configNavbar} />;
  }, [workflow, loading, batchTraceCount]);
  const body = useMemo(() => {
    // Redirecting (see the effect above): render nothing submittable meanwhile.
    if (batchSelectionLost) return <TraceIconSpinner />;
    if (loading) return <TraceIconSpinner />;
    if (!workflow) return <NotFound onHomeClick={goToHome} />;
    if (!(action && workflowContext.author))
      return <NotFound onHomeClick={goToWorkflowOverview} />;

    // resolve the type of interface provided
    // Batch mode (STMN-3359) always uses the standard Form (the server signs
    // each link), so the signature / data-tool / batch-creation interfaces are
    // skipped — notably filter mode arrives with no traceIds, which would
    // otherwise be misread as a trace-creation batch.
    const { traces: { nodes: traces = [] } = {} } = workflow;
    const isDataTool =
      !batchSelection && !!(action.dataImporter || action.dataEditor);
    const isBatchCreation =
      !(batchSelection || traces.length) && action.canBatch;
    const isSignature = !batchSelection && !!action.eSignature;

    switch (true) {
      case isSignature:
        return (
          <Signature
            action={action}
            createLinks={createLinks}
            email={user?.me?.email}
            setCleanupFn={setCleanupFn}
            setFormData={setFormData}
            traces={traces}
            user={user}
            workflowContext={workflowContext}
          />
        );
      case isDataTool:
        // either data editor or data importer interface
        return (
          <DataTool
            action={action}
            disabled={mutationProgress}
            setCleanupFn={setCleanupFn}
            setFormData={setFormData}
            toggleSignDialog={toggleSignDialog}
            traces={workflow.traces.nodes}
            workflowContext={workflowContext}
          />
        );
      case isBatchCreation:
        // batched traces creation
        return (
          <Batch
            action={action}
            disabled={mutationProgress}
            setCleanupFn={setCleanupFn}
            setFormData={setFormData}
            toggleSignDialog={toggleSignDialog}
            workflowContext={workflowContext}
          />
        );
      default:
        // single form (potentially batched updates)
        return (
          <Form
            action={action}
            cacheKey="newLinkForm_"
            disabled={mutationProgress}
            formData={formData}
            onBatchSubmit={batchSelection ? submitBatch : null}
            setCleanupFn={setCleanupFn}
            setFormData={setFormData}
            toggleAnswerGroupDialog={toggleAnswerGroupDialog}
            toggleSignDialog={toggleSignDialog}
            traceCount={batchTraceCount}
            traces={workflow?.traces?.nodes}
            workflowContext={workflowContext}
          />
        );
    }
  }, [
    actionQuery,
    formData,
    toggleSignDialog,
    toggleAnswerGroupDialog,
    mutationProgress,
    workflowContext,
    batchSelection,
    batchSelectionLost,
    batchTraceCount,
    submitBatch
  ]);

  return (
    <>
      {answer && action ? (
        <Form
          action={action}
          actionLinkHash={answerData.actionLinkHash}
          answer
          cacheKey="answerForm_"
          disabled={mutationProgress}
          formData={formData}
          setCleanupFn={setCleanupFn}
          setFormData={setFormData}
          toggleAnswerGroupDialog={toggleAnswerGroupDialog}
          toggleSignDialog={toggleSignDialog}
          traces={answerData.traces}
          workflowContext={workflowContext}
        />
      ) : (
        <>
          <div id={TOOLTIP_PORTAL} />
          <div
            className="fixed top-0 right-0 bottom-0 z-20 flex w-auto flex-row-reverse flex-nowrap data-[is-left=true]:left-0 data-[is-left=true]:[right:unset]"
            id={TRAY_PORTAL_RIGHT}
          />
          {header}
          {body}
        </>
      )}
      {answerGroupDialogOpen && (
        <AnswerGroupDialog
          groups={answerData.groups}
          onClose={toggleAnswerGroupDialog}
          open={answerGroupDialogOpen}
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          toggleSignDialog={toggleSignDialog}
        />
      )}
      {!action?.eSignature && (
        <SignLinkDialog
          description="Sign this action with your private signing key."
          disabled={mutationProgress}
          groupId={workflowContext?.author?.rowId}
          onClose={toggleSignDialog}
          onSendLink={createLinks}
          open={signDialogOpen}
          title="Digital signature"
          workflowId={workflowId} // note: clear local storage if successful ?
        />
      )}
    </>
  );
});

NewLink.propTypes = {
  history: PropType.object.isRequired,
  location: PropType.object.isRequired,
  match: PropType.object.isRequired,
  actionQuery: PropType.object.isRequired,
  user: PropType.object.isRequired,
  createLinksMutation: PropType.func.isRequired,
  setAskBeforeLeaving: PropType.func.isRequired,
  setLeavingAlertModalConfig: PropType.func.isRequired,
  answer: PropType.bool,
  answerData: PropType.object,
  refetchTraceQuery: PropType.func
};

const INIT_ATTESTATION_QUERY = gql`
  query newLinkInitQuery(
    $traceId: UUID
    $actionKey: String
    $groupId: BigInt!
    $formData: JSON
  ) {
    initAttestationLink(
      traceId: $traceId
      action: $actionKey
      groupId: $groupId
      formData: $formData
      type: FREE
      hashed: true
    )
  }
`;
const ACTION_QUERY = gql`
  query actionQuery(
    $actionKey: String!
    $workflowId: BigInt!
    $traceIds: [String!]
  ) {
    workflowByRowId(rowId: $workflowId) {
      ...NewLinkFragment
    }
  }
  ${fragments.actionQuery}
`;

const CREATE_LINKS_MUTATION = gql`
  mutation createLinks($input: [CreateLinkInput!]) {
    createLinks(input: $input) {
      links {
        id
        linkHash
      }
    }
  }
`;
// In batch mode the query string carries a single representative `traceId` (the
// form only ever reads traces[0]); the legacy `traceIds` list is kept for the
// single-trace and pre-batch paths. Neither present means no trace to hydrate —
// `''.split(',')` used to send one empty string as a UUID instead.
const resolveTraceIds = ({ traceId, traceIds }) => {
  if (traceId) {
    return [traceId];
  }
  if (traceIds) {
    return traceIds.split(',');
  }
  return [];
};

export const actionQueryVariable = (match, location, answerData) => {
  const locationSearch = answerData
    ? answerData.location.search
    : qs.parse(location?.search);
  return {
    actionKey: locationSearch.actionKey,
    workflowId: match.params.wfid,
    traceIds: resolveTraceIds(locationSearch)
  };
};

export default compose(
  graphql(ACTION_QUERY, {
    name: 'actionQuery',
    options: ({ match, location, answerData }) => ({
      variables: actionQueryVariable(match, location, answerData),
      fetchPolicy: 'cache-and-network'
    })
  }),
  graphql(CREATE_LINKS_MUTATION, {
    name: 'createLinksMutation',
    options: {
      awaitRefetchQueries: true
    }
  }),
  withUser,
  withRouter,
  withLeavingAlertProvider,
  withLeavingAlertContext
)(NewLink);
